/**
 * Стан мережі (UX-29; states.md, розд. 5): «немає зв'язку» — за фактами, а не лише за
 * `navigator.onLine` (прапорець браузера ненадійний: Wi-Fi є, а інтернету чи сервера за ним немає).
 * - Офлайн: браузер каже `offline`, або запит не дійшов до сервера (`onNetworkFailure` з
 *   `platform/api.ts`, обрив потоку подій) і перевірочний запит теж не дійшов.
 * - Повернення: подія `online` чи чергова перевірка (кожні 5 с, поки зв'язку немає) отримала
 *   будь-яку відповідь сервера, навіть 5xx: мережа є, а збій сервера — окремий стан.
 *
 * Той самий стан бачить TanStack Query (`onlineManager`): офлайн він не шле читань, а з
 * поверненням тихо перечитує видиме (`refetchOnReconnect`); оптимістичні зміни офлайн не робляться
 * (`platform/optimistic.ts`). Прихованої черги змін немає (UX-17, ADR-0002): заблоковану дію людина
 * повторює сама.
 */
import { onlineManager } from '@tanstack/react-query';
import { onNetworkFailure } from './api.ts';

/** Перевірочний запит: живий статус без даних і без входу (edge віддає його назовні). */
export const PROBE_URL = '/healthz';
export const PROBE_EVERY_MS = 5_000;

let lost = false;
let probing = false;
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

const browserOnline = (): boolean => typeof navigator === 'undefined' || navigator.onLine !== false;

/** Є зв'язок: браузер не каже «офлайн» і остання перевірка дійшла до сервера. */
export const isOnline = (): boolean => browserOnline() && !lost;

function emit(): void {
  for (const listener of listeners) listener();
}

function setLost(next: boolean): void {
  if (lost === next) return;
  lost = next;
  emit();
}

function schedule(): void {
  clearTimeout(timer);
  timer = setTimeout(() => void probe(), PROBE_EVERY_MS);
}

/** Перевірити зв'язок: будь-яка відповідь сервера — є; мережева помилка — немає, повторити згодом. */
export async function probe(): Promise<void> {
  if (probing) return;
  probing = true;
  try {
    await fetch(PROBE_URL, { cache: 'no-store', credentials: 'same-origin' });
    clearTimeout(timer);
    timer = undefined;
    setLost(false);
  } catch {
    setLost(true);
    schedule();
  } finally {
    probing = false;
  }
}

/** Запит не дійшов: якщо зв'язок ще вважається наявним, перевірити його (одна перевірка за раз). */
export function reportNetworkFailure(): void {
  if (!lost && browserOnline()) void probe();
}

/** Підписка на зміну стану з фактів (перевірка); події браузера `online` / `offline` — окремо. */
export function subscribeNetwork(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Старт застосунку (`main.tsx`): факти мережі → стан, стан → TanStack Query. */
export function watchNetwork(): () => void {
  const onOnline = () => {
    // Прапорець браузера — лише підказка: чи дійде запит, скаже перевірка.
    emit();
    if (lost) void probe();
  };
  const onOffline = () => emit();
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  const offApi = onNetworkFailure(reportNetworkFailure);
  onlineManager.setEventListener((setOnline) => {
    setOnline(isOnline());
    return subscribeNetwork(() => setOnline(isOnline()));
  });
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    offApi();
    clearTimeout(timer);
    onlineManager.setEventListener(browserEvents);
  };
}

/** Типова поведінка TanStack Query (лише події браузера) — після зупинки спостереження. */
function browserEvents(setOnline: (online: boolean) => void): () => void {
  const on = () => setOnline(true);
  const off = () => setOnline(false);
  window.addEventListener('online', on);
  window.addEventListener('offline', off);
  return () => {
    window.removeEventListener('online', on);
    window.removeEventListener('offline', off);
  };
}

/** Лише для тестів. */
export function resetNetworkForTests(): void {
  clearTimeout(timer);
  timer = undefined;
  lost = false;
  probing = false;
  emit();
}
